import { useState, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Loader2, X, File } from "lucide-react";

export const ResumeUploadModal = ({ onClose, onUploadSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only PDF and DOCX files are allowed");
        return;
      }
      
      setSelectedFile(file);
      if (!resumeName) {
        setResumeName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", resumeName || selectedFile.name);

      const response = await axios.post(`${API}/resumes/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Resume uploaded successfully!");
      onUploadSuccess(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload resume";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="resume-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Upload className="h-5 w-5 text-[#A3C3D8]" />
            Upload Resume
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* File Input Area */}
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              selectedFile 
                ? 'border-[#A3C3D8] bg-[#A3C3D8]/5' 
                : 'border-slate-200 hover:border-[#A3C3D8]'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            {selectedFile ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-lg bg-[#A3C3D8]/10 flex items-center justify-center">
                  <File className="h-6 w-6 text-[#4A728D]" />
                </div>
                <p className="font-medium text-[#2D3748]">{selectedFile.name}</p>
                <p className="text-sm text-[#94A3B8]">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-[#94A3B8]" />
                </div>
                <p className="font-medium text-[#555F67]">Click to upload</p>
                <p className="text-sm text-[#94A3B8]">PDF or DOCX (max 5MB)</p>
              </div>
            )}
          </div>

          {/* Resume Name */}
          <div className="space-y-2">
            <Label htmlFor="resumeName" className="text-[#555F67]">Resume Name</Label>
            <Input
              id="resumeName"
              value={resumeName}
              onChange={(e) => setResumeName(e.target.value)}
              placeholder="e.g., Software Engineer Resume"
              className="border-slate-200 focus:border-[#A3C3D8]"
              data-testid="resume-name-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="border-slate-200">
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
              data-testid="upload-resume-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeUploadModal;
